import { ReactNode, useEffect } from 'react';
import { useMemo, createContext, useCallback, useContext } from 'react';
import { useAccount, useConnect, useSignMessage } from 'wagmi';
import { injected } from 'wagmi/connectors';
import { useFetch } from 'use-http';

import { lsKey, signInLockKey, useUserContext } from './user.context';
import apiEndpoints from '../api/endpoints.constant';
import debounce from 'lodash/debounce';

const WalletContext = createContext<{
  connectWallet: () => void;
  getJwt: () => void;
}>({
  connectWallet: () => {},
  getJwt: () => {},
});

interface IWalletContextProviderProps {
  children: ReactNode;
}

export const WalletContextProvider = ({
  children,
}: IWalletContextProviderProps) => {
  const { address, isConnected, connector } = useAccount();
  const { connect } = useConnect();
  const { signMessage } = useSignMessage();
  const { setIsLoggedIn } = useUserContext();
  const { post: postLoginWallet } = useFetch();
  const { post: postVerifyChallenge } = useFetch();

  async function loginWithWallet() {
    if (localStorage.getItem(signInLockKey)) {
      console.log('Sign-in already in progress in another tab, skipping...');
      return; // Avoid multiple sign requests
    }

    try {
      localStorage.setItem(signInLockKey, 'true'); // Lock sign-in process

      const loginWalletResponse = await postLoginWallet(
        apiEndpoints.loginWallet,
        {
          walletAddress: address,
        },
      ); // Request sign challenge from API

      if (loginWalletResponse?.challenge && address) {
        signMessage(
          { account: address, message: loginWalletResponse.challenge },
          {
            onSuccess: async (signature) => {
              const verifyChallengeResponse = await postVerifyChallenge(
                apiEndpoints.verifyChallenge,
                {
                  challenge: loginWalletResponse.challenge,
                  signature,
                },
              );

              if (verifyChallengeResponse.accessToken) {
                localStorage.setItem(
                  lsKey,
                  verifyChallengeResponse.accessToken,
                );
                setIsLoggedIn(true);
              }
            },
            onError: (error) => {
              console.error(error);
            },
          },
        );
      }
    } finally {
      localStorage.removeItem(signInLockKey); // Remove lock after login attempt
    }
  }

  const connectWallet = useCallback(async () => {
    if (!isConnected || !address) {
      connect({ connector: injected() });
    }
  }, [address, connect, isConnected]);

  const getJwt = debounce(async () => {
    if (!isConnected || !address || !connector || localStorage.getItem(lsKey)) {
      return;
    }

    // Prevent duplicate sign requests
    if (localStorage.getItem(signInLockKey)) {
      console.log('Another tab is handling login, skipping...');
      return;
    }

    await loginWithWallet(); // This now ensures only one sign request happens
  }, 500);

  useEffect(() => {
    // Check for token, only one tab should trigger login
    if (!localStorage.getItem(lsKey)) {
      void getJwt();
    }

    // Listen for changes in localStorage from other tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === lsKey) {
        console.log('Detected login in another tab, updating state...');
        setIsLoggedIn(!!localStorage.getItem(lsKey));
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [address]);

  const contextValue = useMemo(
    () => ({ connectWallet, getJwt }),
    [connectWallet, getJwt],
  );

  return (
    <WalletContext.Provider value={contextValue}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWalletContext = () => useContext(WalletContext);
